import {
  Injectable,
  Inject
} from '@angular/core';
import {
  Http,
  Request,
  RequestMethod,
  RequestOptions,
  RequestOptionsArgs,
  Response,
  Headers
} from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { Observer } from 'rxjs/Observer';
import { Subject } from 'rxjs/Subject';
import {Store} from '@ngrx/store';
import * as fromRoot from '../reducers';
import * as actions from '../actions/authentication';

@Injectable()
export class TokenHttp {
  protected _defOpts: RequestOptions | undefined;

  protected isRefreshing = false;
  protected refresherStream: Subject<boolean> = new Subject<boolean>();
  public refreshTokenStream: Subject<string> = new Subject<string>();
  private config = {
    headerName: 'Authorization',
    headerPrefix: 'Bearer ',
    tokenName: 'access_token',
    tokenGetter: () => sessionStorage.getItem('access_token') as string,
    noJwtError: true,
    noClientCheck: false,
    globalHeaders: [{'Accept': 'application/json', 'Authentication' : 'Bearer ' + sessionStorage.getItem('access_token')}],
    noTokenScheme: false
  };
  public tokenStream: Observable<string>;

  constructor(private http: Http,
              private store: Store<fromRoot.State>,
              @Inject('ApplicationConfig') private globalConfig: any,
              defOpts?: RequestOptions) {
    this.tokenStream = new Observable<string>((obs: any) => {
      obs.next(this.config.tokenGetter());
    });
    this._defOpts = defOpts;
  }

  public request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
    const token: string = sessionStorage.getItem('access_token');

    return this.refreshTheToken(token)
      .mergeMap(() => this.submitRequest(url, options));
  }

  public refreshTheToken(accessToken?: string) {
    // if (!accessToken) {
    //   return Observable.of(accessToken);
    // }

    // if is refreshing token, wait next false value
    if (this.isRefreshing) {
      return Observable.create((observer: Observer<void>) => {
        this.refresherStream
          .subscribe((value) => {
            if (!value) {
              observer.next(null);
              observer.complete();
            }
          });
      });
    } else {
      return this._refreshTheToken();
    }
  }

  private mergeOptions(providedOpts: RequestOptionsArgs, defaultOpts?: RequestOptions) {
    let newOptions = defaultOpts || new RequestOptions();
    if (this.config.globalHeaders) {
      this.setGlobalHeaders(this.config.globalHeaders, providedOpts);
    }

    newOptions = newOptions.merge(new RequestOptions(providedOpts));

    return newOptions;
  }

  private requestHelper(requestArgs: RequestOptionsArgs, additionalOptions?: RequestOptionsArgs): Observable<Response> {
    let options = new RequestOptions(requestArgs);
    if (additionalOptions) {
      options = options.merge(additionalOptions);
    }
    return this.request(new Request(this.mergeOptions(options, this._defOpts)));
  }

  public requestWithToken(req: Request, token: string): Observable<Response> {
    req.headers.set(this.config.headerName, this.config.headerPrefix + token);
    return this.http.request(req);
  }

  public setGlobalHeaders(headers: Array<Object>, request: Request | RequestOptionsArgs) {
    if (!request.headers) {
      request.headers = new Headers();
    }
    headers.forEach((header: Object) => {
      const key: string = Object.keys(header)[0];
      const headerValue: string = (header as any)[key];
      (request.headers as Headers).set(key, headerValue);
    });
  }

  public submitRequest(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
    if (typeof url === 'string') {
      return this.get(url, options); // Recursion: transform url from String to Request
    }

    const req: Request = url as Request;

    return Observable.defer(() => {
      const token: string  = sessionStorage.getItem('access_token');
      return this.requestWithToken(req, token);
    });
  }

  public get(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: '', method: RequestMethod.Get, url: url }, options);
  }

  public post(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: body, method: RequestMethod.Post, url: url }, options);
  }

  public put(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: body, method: RequestMethod.Put, url: url }, options);
  }

  public delete(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: '', method: RequestMethod.Delete, url: url }, options);
  }

  public patch(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: body, method: RequestMethod.Patch, url: url }, options);
  }

  public head(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: '', method: RequestMethod.Head, url: url }, options);
  }

  public options(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return this.requestHelper({ body: '', method: RequestMethod.Options, url: url }, options);
  }

  public uploadFile (url: string, file: File, filename: string): Observable<Response> {
    this.store.dispatch(new actions.SetUploadProgressAction(0));
    return Observable.create((observer: Observer<number>) => {
      const formData: FormData = new FormData(),
        xhr: XMLHttpRequest = new XMLHttpRequest();
      formData.append('file', file, file.name);
      formData.append('name', filename);
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            observer.next(JSON.parse(xhr.response));
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.upload.onprogress = (event) => {
        const progress = Math.round(event.loaded / event.total * 100);
        this.store.dispatch(new actions.SetUploadProgressAction(progress));
      };

      this.refreshTheToken()
        .subscribe(() => {
          xhr.open('POST', url, true);
          const token: string = sessionStorage.getItem('access_token');
          xhr.setRequestHeader(this.config.headerName, this.config.headerPrefix + token);
          xhr.send(formData);
        });

    });
  }

  protected _refreshTheToken() {
    this.setRefreshing(true);
    const idToEmulate = sessionStorage.getItem('aliasId');
    let data = 'grant_type=refresh_token&refresh_token=' + sessionStorage.getItem('refresh_token')
      + '&client_id=' + this.globalConfig.clientId;
    if (idToEmulate) {
      data = data + '&alias=' + idToEmulate;
    }
    const options = new RequestOptions({
      body: data,
      method: RequestMethod.Post,
      url: this.globalConfig.apiEndpoint + '/authtoken',
      headers: new Headers()
    });
    options.headers.set('Content-Type', 'application/x-www-form-urlencoded');
    const req = new Request(this._mergeOptions(options, this._defOpts));
    let request;

    request = this._requestWithToken(req);
    return request
      .flatMap((res: any) => {
        const tokenSetter = ((resp: any): boolean | Promise<void> => {
          resp = resp.json();
          if (!resp['access_token'] || !resp['refresh_token']) {
            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('refresh_token');

            return false;
          }

          sessionStorage.setItem('access_token', resp['access_token']);
          sessionStorage.setItem('refresh_token', resp['refresh_token']);
          return true;
        });
        const onError = Observable.throw('Impossible to get new token');

        if (!tokenSetter(res)) {
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('refresh_token');
          sessionStorage.removeItem('aliasId');
          return onError;
        }

        return Observable.of(res);
      })
      .concatMap((res) => {
        this.setRefreshing(false);
        this.emitRefreshToken();

        return Observable.of(res);
      })
      .catch((res: any) => {
        this.setRefreshing(false);
        this.emitRefreshToken();

        return Observable.of(res);
      });
  }

  protected _mergeOptions(providedOpts: RequestOptionsArgs, defaultOpts?: RequestOptions) {
    let newOptions = defaultOpts || new RequestOptions();
    this.setGlobalHeaders([{'Accept': 'application/json', 'Authentication' : 'Bearer ' + sessionStorage.getItem('access_token')}],
      providedOpts);
    newOptions = newOptions.merge(new RequestOptions(providedOpts));
    return newOptions;
  }

  protected _requestWithToken(req: Request): Observable<Response> {
    return this.httpRequest(req);
  }

  private httpRequest(req: Request) {
    return this.http.request(req);
  }

  private setRefreshing(value: boolean) {
    this.isRefreshing = value;
    this.refresherStream.next(this.isRefreshing);
  }

  private emitRefreshToken() {
    const refreshToken = sessionStorage.getItem('refresh_token');
    this.refreshTokenStream.next(refreshToken);
  }
}



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/core/services/token-http.ts